/* eslint-disable */
/** shared.js — constants, theme context, dev helpers, defaults (App, DevelopmentsTab, devDetailV2, devGrid, settings …) */
import React from "react";
import { isDevOverdue } from "./utils/devOverdue";   // AUD-06 — read inside isOverdue() only (import cycle, see devOverdue.js)

// ════════════════════════════════════════════════════════════════
// CONSTANTS — single source of truth
// ════════════════════════════════════════════════════════════════
export const STAGES = {
  DRAWING:  "Drawing stage",
  HAND:     "Hand made sample stage",
  COLOR:    "Color sample stage",
};
export const DEV_STAGE_STATUS = [STAGES.DRAWING, STAGES.HAND, STAGES.COLOR];

export const STAGE_COLORS = {
  [STAGES.DRAWING]: { color:"#6366f1", bg:"#eef2ff" },
  [STAGES.HAND]:    { color:"#0369a1", bg:"#eff6ff" },
  [STAGES.COLOR]:   { color:"#0891b2", bg:"#ecfeff" },
};

export function devStageColor(stage) {
  return STAGE_COLORS[stage] || { color:"#94a3b8", bg:"#f1f5f9" };
}

export const TYPE_COLORS = {
  DA: { color:"#c2410c", bg:"#fff7ed" },
  DS: { color:"#0369a1", bg:"#eff6ff" },
  DO: { color:"#059669", bg:"#f0fdf4" },
  CL: { color:"#7c3aed", bg:"#f5f3ff" },
};

export const STATUS_COLORS = {
  pending:     "#d97706",
  accepted:    "#0369a1",
  in_progress: "#7c3aed",
  done:        "#16a34a",
  rejected:    "#dc2626",
};

export const PRIORITY_COLORS = {
  low:    "#64748b",
  normal: "#0369a1",
  high:   "#d97706",
  urgent: "#dc2626",
};

// Dev Request workflow stages
export const DEV_REQ_STAGES = [
  { id:"pending",   label:"Received",    icon:"📩", color:"#d97706" },
  { id:"accepted",  label:"Approved",    icon:"✓",  color:"#0369a1" },
  { id:"sketch",    label:"Sketch",      icon:"✏️",  color:"#7c3aed" },
  { id:"first_model",label:"1st Model",  icon:"◈",  color:"#ea580c" },
  { id:"color_model",label:"Color Model",icon:"🎨", color:"#0891b2" },
  { id:"done",      label:"Approved ✓",  icon:"🏁", color:"#16a34a" },
  { id:"rejected",  label:"Rejected",    icon:"✕",  color:"#dc2626" },
];

// Shared style helpers (reduces inline style repetition)
export const flex = (gap=0, align="center", justify="flex-start") => ({
  display:"flex", alignItems:align, justifyContent:justify, ...(gap?{gap}:{})
});
export const flexBetween = (gap=0) => flex(gap, "center", "space-between");
export const pill = (color, bg) => ({
  background: bg||color+"11", color, borderRadius:'var(--radius-pill)',
  padding:"var(--space-2xs) var(--space-md)", fontSize:10, fontWeight:700,
});

// ════════════════════════════════════════════════════════════════
// THEME CONTEXT — eliminates T prop drilling
// ════════════════════════════════════════════════════════════════
export const ThemeCtx = React.createContext(null);
export const useT = () => React.useContext(ThemeCtx);

// ════════════════════════════════════════════════════════════════
// DEV CONSTANTS
// ════════════════════════════════════════════════════════════════

// ============================================================================
// STATUS_DAYS — Canonical vocabulary for DevDetailModal V2 arc (Brief B)
// ============================================================================
// Each stage maps status labels → days-until-deadline.
// null = terminal status (advances stage or completes dev).
// Source of truth: devdetail-prototype-v11.html STAGE_DATA (David approved 2026-05-25).
// Consumed by: useDevDetailV2 (Brief F), useUrgencyV2 (Brief H), Brief C migration.
// Budgets re-approved by David 09.10.26 (DEV-02, from real durations p50/p75 since 26.05). Change only with David's approval.
//
// Vocabulary note: "Proto / Hand made" is David's preferred user-facing label
// for the second stage. Internally, stage_status remains "Hand made sample stage"
// (matches STAGES.HAND) for DB / API / sibling-component compatibility.

export const STATUS_DAYS = {
  [STAGES.DRAWING]: {
    '1st drawing': 14,
    'Designer request': 10,
    'Fty reply': 7,
    'Drawing approved': null, // → advance to STAGES.HAND, first status
  },
  [STAGES.HAND]: {
    'Proto / Hand made requested': 50,
    'Proto / Hand made received': 14,
    'Revise drawing — Designer request': 10,
    'Revise drawing — Fty reply': 7,
    'Revise proto / Hand made — Designer request': 30,
    'Revise proto / Hand made — Fty reply': 7,
    'Stay in Proto / Hand made': null, // → SKIP Color sample, stage_status='Done', skipped_color_sample=true
    'Proto / Hand made approved': null, // → advance to STAGES.COLOR, first status
    'No need Proto / Hand made': null, // → same as approved (advance to STAGES.COLOR)
  },
  [STAGES.COLOR]: {
    'Color samples requested': 70,
    'Revise drawing — Designer request': 10,
    'Revise drawing — Fty reply': 7,
    'Revise colors — Designer request': 21,
    'Revise colors — Fty reply': 7,
    'Colors received': null, // → stage_status='Done', completedAt=now()
  },
};

/**
 * Returns the first (default) status for a given stage.
 * Used when transitioning stages — the next stage begins at its first status.
 */
export function getFirstStatusForStage(stage) {
  const stageMap = STATUS_DAYS[stage];
  if (!stageMap) return null;
  return Object.keys(stageMap)[0] || null;
}

/**
 * Returns the day count allotted for a (stage, status) pair.
 * Returns null for terminal statuses (no countdown — they end the stage/dev).
 * Returns undefined if the pair is unknown (e.g. legacy strings pre-Brief C migration).
 */
export function getDaysForStatus(stage, status) {
  const stageMap = STATUS_DAYS[stage];
  if (!stageMap) return undefined;
  return stageMap[status];
}

/**
 * Returns whether a (stage, status) pair is terminal (ends the stage).
 * Terminal statuses: 'Drawing approved', 'Proto / Hand made approved',
 * 'No need Proto / Hand made', 'Stay in Proto / Hand made', 'Colors received'.
 */
export function isTerminalStatus(stage, status) {
  const days = getDaysForStatus(stage, status);
  return days === null;
}

/**
 * Returns whether a (stage, status) pair both ends the current stage AND
 * skips all remaining stages (i.e. completes the dev directly).
 * Only 'Stay in Proto / Hand made' qualifies in V2.
 */
export function isFullCompletionStatus(stage, status) {
  return stage === STAGES.HAND && status === 'Stay in Proto / Hand made';
}

// ============================================================================
// STATUS_NORMALIZE_MAP_V2 — Legacy → canonical migration map (Brief B → consumed in C)
// ============================================================================
// Maps legacy status strings (from STAGE_STATUSES, DEV_FULL_STATUS, and existing
// STATUS_NORMALIZE_MAP at shared.js:134-149) to STATUS_DAYS canonical values.
// Used by Brief C SQL migration to rewrite the 1,246 existing dev rows.
//
// Ambiguous mappings resolved by David's Q3 (Brief A):
//   'Drawing rounds in progress'        → 'Designer request'
//   'Shape review in progress'           → 'Proto / Hand made received'
//   'Color samples received — under review' → 'Color samples requested'

export const STATUS_NORMALIZE_MAP_V2 = {
  // Drawing stage — STAGE_STATUSES legacy
  'Waiting for first drawing':        { stage: STAGES.DRAWING, status: '1st drawing' },
  'Drawing rounds in progress':       { stage: STAGES.DRAWING, status: 'Designer request' },
  // Drawing stage — DEV_FULL_STATUS legacy
  'Waiting for drawing from factory': { stage: STAGES.DRAWING, status: '1st drawing' },
  'Drawing received':                 { stage: STAGES.DRAWING, status: 'Designer request' },
  'Drawing approved — moving to hand sample': { stage: STAGES.DRAWING, status: 'Drawing approved' },

  // Hand made sample stage — STAGE_STATUSES legacy
  'Waiting for hand sample':          { stage: STAGES.HAND, status: 'Proto / Hand made requested' },
  'Shape review in progress':         { stage: STAGES.HAND, status: 'Proto / Hand made received' },
  // Hand made sample stage — DEV_FULL_STATUS legacy
  'Waiting for hand sample from factory': { stage: STAGES.HAND, status: 'Proto / Hand made requested' },
  'Hand sample received':             { stage: STAGES.HAND, status: 'Proto / Hand made received' },

  // Color sample stage — STAGE_STATUSES legacy
  'Waiting for color sample':         { stage: STAGES.COLOR, status: 'Color samples requested' },
  'Color review in progress':         { stage: STAGES.COLOR, status: 'Color samples requested' },
  // Color sample stage — DEV_FULL_STATUS legacy
  'Waiting for color samples from factory':    { stage: STAGES.COLOR, status: 'Color samples requested' },
  'Color samples received — under review':     { stage: STAGES.COLOR, status: 'Color samples requested' },
  'Color samples confirmed ✓ — done':          { stage: STAGES.COLOR, status: 'Colors received' },
  'Color samples confirmed ✓ — DONE':          { stage: STAGES.COLOR, status: 'Colors received' },
};

/**
 * Normalizes a legacy status string to its V2 canonical (stage, status) pair.
 * Returns null if the input is not a known legacy value.
 *
 * Note: STATUS_NORMALIZE_MAP (legacy, shared.js:134-149) normalizes to legacy
 * canonical strings (within the v1 vocabulary). STATUS_NORMALIZE_MAP_V2 takes
 * those AND the v1 canonical strings AND maps them all to V2 vocabulary.
 *
 * Pipeline: raw → STATUS_NORMALIZE_MAP (legacy alias resolution) → STATUS_NORMALIZE_MAP_V2 (V2 canonicalization).
 */
export function normalizeStatusToV2(rawStatus) {
  if (!rawStatus || typeof rawStatus !== 'string') return null;
  // First pass: legacy alias resolution (typo/case fixes)
  const legacyCanonical = STATUS_NORMALIZE_MAP[rawStatus.toLowerCase()] || rawStatus;
  // Second pass: V2 canonicalization
  return STATUS_NORMALIZE_MAP_V2[legacyCanonical] || null;
}

// ============================================================================
// Urgency thresholds (Brief B → consumed by useUrgencyV2 in Brief H)
// ============================================================================
// Per David's Q1 (Brief A): 'urgent' at 80% of allotted days elapsed, 'overdue' at 100%+.

export const URGENCY_THRESHOLDS = {
  URGENT_PCT:  0.80,
  OVERDUE_PCT: 1.00,
};

/**
 * Computes urgency state for an active dev's current status.
 *
 * @param {string} stage - dev's current stage_status (must be a STAGES.* value)
 * @param {string} status - dev's current full_status
 * @param {string|Date} statusSetAt - ISO timestamp or Date when current status was set
 * @returns {{state: 'normal'|'urgent'|'overdue'|'unknown', daysElapsed: number, daysAllotted: number|null, pct: number, remaining: number}}
 *
 * Returns state='unknown' if:
 *   - stage/status not in STATUS_DAYS
 *   - status is terminal (no deadline applicable)
 *   - statusSetAt missing or unparseable
 */
export function getStatusUrgency(stage, status, statusSetAt) {
  const daysAllotted = getDaysForStatus(stage, status);
  if (daysAllotted === undefined || daysAllotted === null) {
    return { state: 'unknown', daysElapsed: 0, daysAllotted: daysAllotted ?? null, pct: 0, remaining: 0 };
  }
  if (!statusSetAt) {
    return { state: 'unknown', daysElapsed: 0, daysAllotted, pct: 0, remaining: daysAllotted };
  }
  const set = typeof statusSetAt === 'string' ? new Date(statusSetAt) : statusSetAt;
  if (isNaN(set.getTime())) {
    return { state: 'unknown', daysElapsed: 0, daysAllotted, pct: 0, remaining: daysAllotted };
  }
  const daysElapsed = (Date.now() - set.getTime()) / 86400000;
  const pct = daysElapsed / daysAllotted;
  const remaining = daysAllotted - daysElapsed;
  let state;
  if (pct >= URGENCY_THRESHOLDS.OVERDUE_PCT) state = 'overdue';
  else if (pct >= URGENCY_THRESHOLDS.URGENT_PCT) state = 'urgent';
  else state = 'normal';
  return { state, daysElapsed, daysAllotted, pct: Math.min(pct, 1.5), remaining };
}

/**
 * @deprecated Will be removed in Brief K cleanup after V2 cutover.
 * V2 callers should use STATUS_DAYS (Brief B) instead.
 * Brief F migrates the remaining write paths (DevFormModal2, DevelopmentsTab,
 * AcceptRequestFlow, devImportUtils, DevImportModal, DevImportConflictModal,
 * DevConflictCard) off this constant.
 * Until V2 cutover (Brief J), this remains the canonical v1 vocabulary.
 */
export const DEV_FULL_STATUS = {
  [STAGES.DRAWING]: [
    "Waiting for drawing from factory",
    "Drawing received",
    "Drawing approved — moving to hand sample",
  ],
  [STAGES.HAND]: [
    "Waiting for hand sample from factory",
    "Hand sample received",
  ],
  [STAGES.COLOR]: [
    "Waiting for color samples from factory",
    "Color samples received — under review",
    "Color samples confirmed ✓ — done",
  ],
};

// Stopped = deliberate decision to end development. Treated like completion for stats.
export const STOPPED_STATUSES = [
  "Stopped at Drawing stage",
  "Stopped at Hand sample stage",
  "Stopped at Color stage",
  "Completed at Drawing stage",
  "Completed at Hand Made stage",
  "Completed at Color Sample stage",
  "Cancelled",
  "Paused",
];

// Next status within same stage — smart progression
export function getNextStatus(stage, currentStatus) {
  const list = DEV_FULL_STATUS[stage] || [];
  const idx = list.indexOf(currentStatus);
  if (idx >= 0 && idx < list.length - 1) return list[idx + 1];
  return null;
}

// Is this a "stopped" development (cancelled, paused, or legacy stopped — NOT completed-at)
export function isDevStopped(dev) {
  const s = (dev.full_status || "").toLowerCase();
  return s.startsWith("stopped") || s === "cancelled" || s === "paused";
}

// Completed = natural completion OR completed-via-stop (not cancelled/paused)
export function isDevCompleted(dev) {
  const s = (dev.full_status || "").toLowerCase();
  return s.includes("confirmed ✓") || s.includes("done") || s.startsWith("stopped") || s.startsWith("completed at");
}

export function isTransitionStatus(full_status) {
  const s = (full_status||"").toLowerCase();
  return s.includes("→") || s.includes("done");
}

/**
 * @deprecated Used internally by normalizeStatusToV2() during V2 migration.
 * Will be removed in Brief K cleanup. Direct consumers should call
 * normalizeStatusToV2(rawStatus) instead.
 */
export const STATUS_NORMALIZE_MAP = {
  "waiting for first drawing from factory":                      "Waiting for drawing from factory",
  "waiting for drawing from factory":                            "Waiting for drawing from factory",
  "recived drawing from factory - designer need to replay":      "Drawing received — pending designer reply",
  "received drawing from factory - designer need to replay":     "Drawing received — pending designer reply",
  "drawing received — pending designer reply":                   "Drawing received — pending designer reply",
  "drawing is approved - waiting for hand made sample from fty": "Drawing approved ✓ → awaiting hand sample",
  "drawing approved ✓ → awaiting hand sample":                   "Drawing approved ✓ → awaiting hand sample",
  "hand made sample recived and confirmed":                      "Hand sample confirmed ✓ → awaiting color samples",
  "hand sample confirmed ✓ → awaiting color samples":            "Hand sample confirmed ✓ → awaiting color samples",
  "waiting for color sample to sent from factory":               "Waiting for color samples from factory",
  "waiting for color samples from factory":                      "Waiting for color samples from factory",
  "color samples arrvied and confirmed":                         "Color samples confirmed ✓ — DONE",
  "color samples arrived and confirmed":                         "Color samples confirmed ✓ — DONE",
  "color samples confirmed ✓ — done":                            "Color samples confirmed ✓ — DONE",
};
export function normalizeStatus(s) {
  return STATUS_NORMALIZE_MAP[(s||"").toLowerCase()] || s || "";
}

export const STAGE_NORMALIZE_MAP = {
  "drawing stage":            "Drawing stage",
  "hand made sample stage":   "Hand made sample stage",
  "color sample stage":       "Color sample stage",
  "colour sample stage":      "Color sample stage",
  "color samples stage":      "Color sample stage",
  "done":                     "Done",
  "completed":                "Done",
};
export function normalizeStageStatus(s) {
  return STAGE_NORMALIZE_MAP[(s||"").toLowerCase().trim()] || s || "";
}

// AUD-06 (gate J): one overdue rule, in utils/devOverdue.js. The dev_date / overdueRules rule that
// lived here is gone. `overdueRules` now only says the caller asked: DevCardNew calls without it,
// which has always returned false, and still does.
export function isOverdue(dev, overdueRules) {
  if (!overdueRules) return false;
  return isDevOverdue(dev);
}

export const DEFAULT_OVERDUE_RULES = {
  [STAGES.DRAWING]: { enabled:true, days:14, refDate:"dev_date" },
  [STAGES.HAND]:    { enabled:true, days:30, refDate:"dev_date" },
  [STAGES.COLOR]:   { enabled:true, days:21, refDate:"dev_date" },
};

export const DEV_TYPES_LIST   = ["DA","DS","DO","CL"];
export const DEV_SEASONS      = ["Winter","Spring","Summer","Fall"];
export const DEV_MATERIALS    = ["ACETATE","METAL","TR","PLASTIC","TITANIUM"];
// Canonical "CODE — Name" (em-dash with spaces), matching qc_suppliers — David's decision
// 2026-07-17. Was bare codes ("IP","SK",…), which is the RE-DRIFT SOURCE that undid the
// 2026-04-26 normalize-01 migration: this constant is the fallback for
// dev_settings.devFields[fty].options (DevFormModal2 gOpts + devGridEdit FALLBACK +
// DEFAULT_DEV_FIELDS below), so if that config ever emptied, the form would start writing
// bare codes again and re-create the duplicates. Live config already holds CODE — Name;
// this makes the safety net agree with it. Pairs with sql/dev-factory-normalize-02.sql.
// NOTE: 'PS — Phoenix' is the corrected spelling (the roster seed's "Phonix" is a typo the
// live table has since fixed). 'FR — Flexy' is the one name taken from the seed rather than
// David's live Q5 output — confirm if FR is ever actually used.
export const DEV_FACTORIES    = ["IP — Inter","SK — Sunrise","LN — Lanson","PS — Phoenix","SJ — Sojos","LA — Longvision","FR — Flexy","TD — Tongda","AD — Aishi","BV — Brilliant Vision","Other"];
// T-1051 — "Brand Image / Reference" renamed to "Image / Fty Sample" with sub_type
// dispatch (brand | fty) inside based_on_data. See sql/dev-based-on-01-rename-and-sub-type.sql
// for the one-shot rename migration; reads in src/utils/devCardUtils.js getBoData()
// + src/components/.../useDevDetail.js + OverviewTab.js + CompletionView.js +
// compareUtils.js dispatch on sub_type with 'brand' as the legacy-tolerance default.
export const DEV_BASED_ON_LIST= ["Original Item (Catalog)","Store Model (CL)","Image / Fty Sample","Other"];

export const DEFAULT_DEV_FIELDS = [
  { key:"dev_number",   label:"DO No.",       type:"text",     required:true,  isDefault:true, section:"main" },
  { key:"dev_type",     label:"Dev. Type",    type:"select",   required:true,  isDefault:true, options:["DA","DS","DO","CL"], section:"main" },
  { key:"fty",          label:"Factory",      type:"select",   required:false, isDefault:true, options:DEV_FACTORIES, section:"main" },
  { key:"sun_optic",    label:"Sun / Optic",  type:"select",   required:true,  isDefault:true, options:["Sunglasses","Optic"], section:"main" },
  { key:"designer",     label:"Designer",     type:"select",   required:false, isDefault:true, options:[], section:"main" },
  { key:"material",     label:"Material",     type:"select",   required:false, isDefault:true, options:DEV_MATERIALS, section:"main" },
  { key:"shape1",       label:"Shape 1",      type:"select",   required:false, isDefault:true, options:[], section:"main" },
  { key:"shape2",       label:"Shape 2",      type:"select",   required:false, isDefault:true, options:[], section:"main" },
  { key:"shape3",       label:"Shape 3",      type:"select",   required:false, isDefault:true, options:[], section:"main" },
  { key:"gender",         label:"Gender",         type:"select",   required:false, isDefault:true, options:["Men","Women","Unisex"], section:"main" },
  { key:"season",       label:"Season",       type:"select",   required:false, isDefault:true, options:DEV_SEASONS, section:"main" },
  { key:"year",         label:"Year",         type:"text",     required:false, isDefault:true, section:"main" },
  { key:"dev_date",     label:"Dev. Date",    type:"date",     required:false, isDefault:true, section:"main" },
  { key:"drawing_date", label:"Drawing Date", type:"date",     required:false, isDefault:true, section:"main" },
  { key:"stage_status", label:"Stage",        type:"stage",    required:true,  isDefault:true, section:"main" },
  { key:"based_on",     label:"Based On",     type:"select",   required:false, isDefault:true, options:DEV_BASED_ON_LIST, section:"main" },
  { key:"po_number",    label:"PO Number",    type:"text",     required:false, isDefault:true, section:"main" },
  { key:"ordered",      label:"Ordered",      type:"checkbox", required:false, isDefault:true, section:"main" },
  { key:"cl_assigned",  label:"CL Assigned",  type:"checkbox", required:false, isDefault:true, section:"main" },
  { key:"notes",        label:"Notes",        type:"textarea", required:false, isDefault:true, section:"main" },
];

export function generateDevNumber(type, existing) {
  const year = new Date().getFullYear();
  const prefix = `${type}-${year}-`;
  const nums = existing
    .filter(d => d.dev_number && d.dev_number.startsWith(prefix))
    .map(d => parseInt(d.dev_number.split("-").pop()) || 0);
  const next = (nums.length ? Math.max(...nums) : 0) + 1;
  return `${prefix}${String(next).padStart(3, "0")}`;
}

// ════════════════════════════════════════════════════════════════
// CATALOG CONSTANTS
// ════════════════════════════════════════════════════════════════
// SEED_ITEMS removed — mock data not needed in production

export const SHAPE_OPTIONS = ["70's","Cateye","Club","Double bridge","Fashion","Fem","Frameless","Geo","Mask","Men","New Fem","Oval","Pilot","Racer","Rectangle","Round","Sport","Square","Trendy"];

export const MATERIAL_SUB_OPTIONS = {
  "Metal":     ["Aluminum","Titanium","Frameless","Memory","Mix Metal & Acetate"],
  "Injection": ["TR-90","Ultem","TPE","PC","Mixed Metal"],
  "Acetate":   ["Acetate","Acetate with Metal"],
};

export const DEFAULT_FIELDS = [
  { key:"brand",          label:"Brand",                 type:"select", required:true,  isDefault:true, options:["Ace & Tate","Ahlam","Alain Mikli","Anne&Valentin","Balenciaga","Bottega Veneta","Burberry","Cartier","Cazal","Chanel","Chloe","Christian Dior","Dunhill","Fendi","Gentle Monster","Giorgio Armani","Givenchy","Gucci","Hoffmann Natural Eyewear","Hugo Boss","IC Berlin","Jacques Marie Mage","Jimmy Choo","Lindberg","Loewe","Maison Margiela","Miu Miu","Mykita","Oakley","Oliver Peoples","Other","Prada","Ray-Ban","Roberto Cavalli","Saint Laurent","Salvatore Ferragamo","Silhouette","Stella McCartney","Thom Browne","Tom Ford","Valentino","Versace","Vogue"] },
  { key:"frameCategory",  label:"Optic / Sunglasses",    type:"select", required:false, isDefault:true, options:["Optic","Sunglasses"] },
  { key:"origin",         label:"Origin",                type:"select", required:false, isDefault:true, options:["ASIA","CheapLine","DE","FTY","IL","ITALY","NL","OG GUY"] },
  { key:"material",       label:"Material",              type:"select", required:false, isDefault:true, options:["Metal","Injection","Acetate"] },
  { key:"materialDetail", label:"Material Detail",       type:"select", required:false, isDefault:true, options:[] },
  { key:"gender",         label:"Gender",                type:"select", required:false, isDefault:true, options:["Men","Women","Unisex","Kids"] },
  { key:"shape1",         label:"Shape 1",               type:"select", required:false, isDefault:true, options:SHAPE_OPTIONS },
  { key:"shape2",         label:"Shape 2",               type:"select", required:false, isDefault:true, options:SHAPE_OPTIONS },
  { key:"shape3",         label:"Shape 3",               type:"select", required:false, isDefault:true, options:SHAPE_OPTIONS },
  { key:"status",         label:"Status",                type:"select", required:false, isDefault:true, options:["In Development","Developed","Archived","Ordered"] },
  { key:"assignedTo",     label:"Assigned To",           type:"select", required:false, isDefault:true, options:["Warehouse","Noah","Noam","Almog","Noa","David","Yaeli","Danielle","Kayla","Yoyo","Order/fty/Daniel"] },
  { key:"developmentOrder", label:"Development Order Ref", type:"text", required:false, isDefault:true, options:[] },
  { key:"sku",            label:"SKU / Ref. Code",       type:"text",   required:false, isDefault:true, options:[] },
  { key:"location",       label:"Warehouse Location",    type:"text",   required:false, isDefault:true, options:[] },
  { key:"color",          label:"Color / Finish",        type:"text",   required:false, isDefault:true, options:[] },
  { key:"price",          label:"Price",                 type:"text",   required:false, isDefault:true, options:[] },
  { key:"receivedDate",   label:"Received Date",         type:"month",  required:false, isDefault:true, options:[] },
  { key:"notes",          label:"Notes",                 type:"textarea", required:false, isDefault:true, options:[] },
];

export const THEME_PRESETS = [
  { name:"CL Berlin", accent:"#0a0a0a", accentText:"#ffffff", mainBg:"#f7f7f5", cardBg:"#ffffff", sidebarBg:"#131312", border:"#e8e8e4", textPrimary:"#0a0a0a", textSecondary:"#555555", textMuted:"#999999" },
  { name:"Slate",  accent:"#1e293b", accentText:"#ffffff", mainBg:"#f1f5f9", cardBg:"#ffffff", sidebarBg:"#ffffff", border:"#e2e8f0", textPrimary:"#0f172a", textSecondary:"#64748b", textMuted:"#94a3b8" },
  { name:"Ocean",  accent:"#0369a1", accentText:"#ffffff", mainBg:"#f0f9ff", cardBg:"#ffffff", sidebarBg:"#ffffff", border:"#bae6fd", textPrimary:"#0c4a6e", textSecondary:"#0369a1", textMuted:"#7dd3fc" },
  { name:"Forest", accent:"#166534", accentText:"#ffffff", mainBg:"#f0fdf4", cardBg:"#ffffff", sidebarBg:"#ffffff", border:"#bbf7d0", textPrimary:"#052e16", textSecondary:"#166534", textMuted:"#86efac" },
  { name:"Rose",   accent:"#9f1239", accentText:"#ffffff", mainBg:"#fff1f2", cardBg:"#ffffff", sidebarBg:"#ffffff", border:"#fecdd3", textPrimary:"#4c0519", textSecondary:"#9f1239", textMuted:"#fda4af" },
  { name:"Dark",   accent:"#a78bfa", accentText:"#1e1b4b", mainBg:"#0f172a", cardBg:"#1e293b", sidebarBg:"#0f172a", border:"#334155", textPrimary:"#f1f5f9", textSecondary:"#94a3b8", textMuted:"#475569" },
  { name:"Amber",  accent:"#b45309", accentText:"#ffffff", mainBg:"#fffbeb", cardBg:"#ffffff", sidebarBg:"#ffffff", border:"#fde68a", textPrimary:"#451a03", textSecondary:"#92400e", textMuted:"#fcd34d" },
];

// ── Carolina Lemke Brand Fonts ───────────────────────────────────────────────
const CL_FONT_CSS = `
@font-face { font-family:'Arquitecta'; src:url('/fonts/Arquitecta.otf') format('opentype'); font-weight:400; font-style:normal; font-display:swap; }
body { -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaLight.otf') format('opentype'); font-weight:300; font-style:normal; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaMedium.otf') format('opentype'); font-weight:500; font-style:normal; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaBold.otf') format('opentype'); font-weight:700; font-style:normal; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaHeavy.otf') format('opentype'); font-weight:800; font-style:normal; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaBlack.otf') format('opentype'); font-weight:900; font-style:normal; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaThin.otf') format('opentype'); font-weight:100; font-style:normal; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaBook.otf') format('opentype'); font-weight:350; font-style:normal; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/Arquitecta-Italic.otf') format('opentype'); font-weight:400; font-style:italic; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaBold-Italic.otf') format('opentype'); font-weight:700; font-style:italic; font-display:swap; }
@font-face { font-family:'Arquitecta'; src:url('/fonts/ArquitectaHeavy-Italic.otf') format('opentype'); font-weight:800; font-style:italic; font-display:swap; }
@font-face { font-family:'SimplerPro'; src:url('/fonts/SimplerPro_V3-Light.otf') format('opentype'); font-weight:300; font-style:normal; font-display:swap; }
@font-face { font-family:'SimplerPro'; src:url('/fonts/SimplerPro_V3-LightItalic.otf') format('opentype'); font-weight:300; font-style:italic; font-display:swap; }
@font-face { font-family:'FuturaStd'; src:url('/fonts/FuturaStd-Heavy.otf') format('opentype'); font-weight:700; font-style:normal; font-display:swap; }
`;
// Inject brand fonts once
if (!document.getElementById("clb-brand-fonts")) {
  const st = document.createElement("style");
  st.id = "clb-brand-fonts";
  st.textContent = CL_FONT_CSS;
  document.head.appendChild(st);
}

export const FONT_PRESETS = [
  { name:"CL Brand",  display:"'Arquitecta',system-ui,sans-serif",        body:"'Arquitecta',system-ui,sans-serif",   label:"Arquitecta (CL Brand)" },
  { name:"CL Style",  display:"'Montserrat',system-ui,sans-serif",        body:"'Montserrat',system-ui,sans-serif",   label:"Montserrat",      gf:"Montserrat:wght@300;400;500;600;700;800" },
  { name:"Classic",   display:"Georgia,'Times New Roman',serif",          body:"system-ui,sans-serif",               label:"Georgia / System"     },
  { name:"Luxury",    display:"'Playfair Display',Georgia,serif",         body:"'Lato',system-ui,sans-serif",         label:"Playfair / Lato",      gf:"Playfair+Display:wght@400;700&family=Lato:wght@400;600;700" },
  { name:"Modern",    display:"'DM Serif Display',Georgia,serif",         body:"'DM Sans',system-ui,sans-serif",      label:"DM Serif / DM Sans",   gf:"DM+Serif+Display&family=DM+Sans:wght@400;500;600;700" },
  { name:"Clean",     display:"'Josefin Sans',system-ui,sans-serif",      body:"'Josefin Sans',system-ui,sans-serif", label:"Josefin Sans",          gf:"Josefin+Sans:wght@400;600;700" },
];

export const DEFAULT_THEME = { ...THEME_PRESETS[0], preset:"CL Berlin", fontPreset:"CL Brand", displayFont:FONT_PRESETS[0].display, bodyFont:FONT_PRESETS[0].body, borderRadius:'var(--radius-md)', fontScale:1, hoverPreviewEnabled:true };

export const STATUS_CONF = {
  "In Stock":   { bg:"#dcfce7", text:"#166534", dot:"#22c55e" },
  "On Display": { bg:"#dbeafe", text:"#1e40af", dot:"#3b82f6" },
  "Loaned Out": { bg:"#fef9c3", text:"#854d0e", dot:"#eab308" },
  "Retired":    { bg:"#fee2e2", text:"#991b1b", dot:"#ef4444" },
};

export const ROLES = {
  admin:  { label:"Admin",  color:"#f59e0b", canEdit:true,  canDelete:true,  canAdd:true,  canExport:true,  canSettings:true  },
  editor: { label:"Editor", color:"#6366f1", canEdit:true,  canDelete:false, canAdd:true,  canExport:true,  canSettings:false },
  viewer: { label:"Viewer", color:"#10b981", canEdit:false, canDelete:false, canAdd:false, canExport:false, canSettings:false },
};
